import { union } from 'lodash';
import { useMemo } from 'react';
import { UserClient } from 'clients';
import { QueryKeys, UserRole } from 'enums';
import { UseQueryBaseSearchOptions } from 'hooks/api/query/types';
import { useQuerySearch } from 'hooks/api/query/useQuery';
import { User, UserSearch } from 'types';

export function useSearchUsersByRole(role: UserRole, props?: UseQueryBaseSearchOptions<User, UserSearch>) {
  const { filter, queryOptions, sorter, enabled } = props || {};
  return useQuerySearch({
    queryKey: QueryKeys.Users,
    clientClass: UserClient,
    enabled,
    filter: { ...filter, role },
    sorter: sorter ?? ((a, b) => a.email.localeCompare(b.email)),
    queryOptions: {
      // Default user search query options should be passed here for example:   cacheTime: 10 * 60 * 60 * 1000,
      ...queryOptions,
    },
  });
}

export function useSearchEndUser(searchUserFilter?: Partial<UserSearch>) {
  const { role: userRoleFilter, ...filter } = searchUserFilter || {};
  const shouldFetchCapitolaBroker = !userRoleFilter || userRoleFilter === UserRole.CapitolaBroker;
  const shouldFetchBroker = !userRoleFilter || userRoleFilter === UserRole.Broker;
  const shouldFetchRetailer = !userRoleFilter || userRoleFilter === UserRole.Retailer;

  const { items: capitolaBrokers, refetch: refetchCapitolaBrokers } = useSearchUsersByRole(UserRole.CapitolaBroker, {
    enabled: shouldFetchCapitolaBroker,
    filter,
  });
  const { items: brokers, refetch: refetchBroker } = useSearchUsersByRole(UserRole.Broker, {
    enabled: shouldFetchBroker,
    filter,
  });
  const { items: retailers, refetch: refetchRetailer } = useSearchUsersByRole(UserRole.Retailer, {
    enabled: shouldFetchRetailer,
    filter,
  });

  const refetch = async () => {
    await Promise.all(
      [
        shouldFetchCapitolaBroker && refetchCapitolaBrokers(),
        shouldFetchBroker && refetchBroker(),
        shouldFetchRetailer && refetchRetailer(),
      ].filter(Boolean),
    );
  };

  const users = useMemo(
    () =>
      union(
        shouldFetchCapitolaBroker ? capitolaBrokers : [],
        shouldFetchBroker ? brokers : [],
        shouldFetchRetailer ? retailers : [],
      ),
    [brokers, capitolaBrokers, retailers, shouldFetchBroker, shouldFetchCapitolaBroker, shouldFetchRetailer],
  );

  return { users, refetch };
}
